import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, mergeProps as _mergeProps, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref, watch,
} from 'vue';
import { debounce, throttle } from 'lodash';
import OpenSeadragon, { MouseTracker } from 'openseadragon';
import Moveable, { MoveableEvents, MoveableInterface } from 'vue3-moveable';
import {
  injectionKey,
  OSDExposition,
  OverlayEventMap,
} from '@/core/components/data-display/OSD/osd.shared';

type AddOverlayArgs = Parameters<OpenSeadragon.Viewer['addOverlay']>


export default /*#__PURE__*/_defineComponent({
  __name: 'NkOSDOverlay',
  props: {
  highlight: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  injectionKey: {
    type: Symbol,
    default: injectionKey,
  },
  resizable: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object as PropType<{
      location: AddOverlayArgs[1],
      placement: AddOverlayArgs[2],
      onDraw: AddOverlayArgs[3],
    }>,
    default: () => ({}),
  },
},
  emits: ["update", "mounted", "click", "zoom"],
  setup(__props, { emit }: { emit: ({
  (event: 'update', value: Parameters<OverlayEventMap['update']>[0]): void,
  (event: 'mounted'): void,
  (event: 'click', value: Parameters<OverlayEventMap['click']>[0]): void,
  (event: 'zoom', value: number | undefined): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props

const el = ref<HTMLElement>();
const moveable = ref<MoveableInterface>();





const injected = inject<OSDExposition>(props.injectionKey);
const isViewportChanging = ref(false);
const getViewer = () => {
  if (injected) {
    return injected.getViewer();
  }
  return null;
};

const mouseTracker = ref<MouseTracker | undefined>();
const updateMoveableTarget = throttle(() => {
  // moveable.value?.updateTarget();
  // moveable.value?.updateRect();
}, 16);

// It proved a complicated to update the moveable target and the overlay position at the same time
// due to the convertions we need from the viewport on every tick. Instead we'll only update
// the moveable target separately and update the overlay on moveable's "resizeEnd" event.
const updateMoveablePosition = (event: MoveableEvents['resize']) => {
  const viewer = getViewer();

  if (viewer && el.value) {
    const { beforeTranslate } = event.drag;
    el.value.style.width = `${ event.width }px`;
    el.value.style.height = `${ event.height }px`;
    el.value.style.transform = `translate(${ beforeTranslate[0] }px, ${ beforeTranslate[1] }px)`;
  }
};

const updateOverlayPosition = ({
                                 lastEvent: event,
                                 moveable: eventMoveable,
                               }: MoveableEvents['resizeEnd']) => {
  const moveableRect = eventMoveable.getRect();
  const viewer = getViewer();

  if (viewer && el.value) {
    const overlayPositionRect = viewer.getOverlayById(el.value)
      .getBounds(viewer.viewport);
    const overlayPositionPixelPoint = viewer.viewport.deltaPixelsFromPoints(new OpenSeadragon.Point(overlayPositionRect.x, overlayPositionRect.y));
    const positionPixelPoint = new OpenSeadragon.Point(overlayPositionPixelPoint.x + event.drag.dist[0], overlayPositionPixelPoint.y + event.drag.dist[1]);
    const sizePixelPoint = new OpenSeadragon.Point(moveableRect.width, moveableRect.height);
    const positionPoint = viewer.viewport.deltaPointsFromPixels(positionPixelPoint);
    const sizePoint = viewer.viewport.deltaPointsFromPixels(sizePixelPoint);
    const newLocation = new OpenSeadragon.Rect(
      positionPoint.x,
      positionPoint.y,
      sizePoint.x,
      sizePoint.y,
    );
    viewer.updateOverlay(
      el.value,
      newLocation,
    );
  }
};
const onLocationChange = (event: OpenSeadragon.UpdateOverlayEvent) => {
  const viewer = getViewer();
  if (viewer && event.element as HTMLElement === el.value) {
    const pixelLocationPoint = viewer.viewport.viewportToImageCoordinates(new OpenSeadragon.Point(event.location.x, event.location.y));
    const pixelSizePoint = viewer.viewport.viewportToImageCoordinates(new OpenSeadragon.Point('width' in event.location ? event.location.width : 0, 'height' in event.location ? event.location.height : 0));
    const value = {
      event,
      ...event.location,
      pixelX: pixelLocationPoint.x,
      pixelY: pixelLocationPoint.y,
      pixelWidth: pixelSizePoint.x,
      pixelHeight: pixelSizePoint.y,
    };
    emit('update', value as any);
  }
};

const onOverlayAdded = (event: OpenSeadragon.AddOverlayEvent) => {
  const viewer = getViewer();
  let hasDragged = false;

  if (viewer && event.element as HTMLElement === el.value) {
    emit('mounted');
    mouseTracker.value = new OpenSeadragon.MouseTracker({
      element: event.element,
      dragHandler: () => {
        hasDragged = true;
      },
      clickHandler: (e) => {
        if (hasDragged) {
          hasDragged = false;
          return false;
        }

        return emit('click', {
          initialEvent: e,
          location: viewer?.getOverlayById(event.element)
            .getBounds(viewer?.viewport),
        });
      },
    });
  }
};

const moveableUpdateEvents: (keyof OpenSeadragon.ViewerEventMap)[] = ['open', 'animation-start'];

const addOverlay = (viewer: OpenSeadragon.Viewer, element: HTMLElement, options: typeof props.options) => {
  const {
    location,
    placement,
    onDraw,
  } = options;

  viewer.addOverlay(element, location, placement, onDraw);
};

const removeOverlay = (viewer: OpenSeadragon.Viewer, element: HTMLElement) => {
  viewer.removeOverlay(element);
};

// const setViewportChangingValue = (value: boolean) => {
//   isViewportChanging.value = value;
//   // moveable.value?.updateRect();
// };
// const onViewChangingEnd = debounce(setViewportChangingValue.bind(undefined, false), 100);

const lastViewportChangeDate = ref((new Date()).toString());
let timer: ReturnType<typeof setTimeout> | null = null;
watch(() => lastViewportChangeDate.value, () => {
  isViewportChanging.value = true;
  if (timer) {
    clearTimeout(timer);
  }

  timer = setTimeout(() => {
    isViewportChanging.value = false;
  }, 750);
})

const onZoom = debounce(() => {
  emit('zoom', getViewer()?.viewport.getZoom(true));
}, 200);

const addEventListeners = (viewer: OpenSeadragon.Viewer) => {
  viewer.addHandler('add-overlay', onOverlayAdded);
  viewer.addHandler('update-overlay', onLocationChange);
  // viewer.addHandler('viewport-change', setViewportChangingValue.bind(undefined, true));
  // viewer.addHandler('viewport-change', onViewChangingEnd);
  viewer.addHandler('viewport-change', () => { lastViewportChangeDate.value = (new Date()).toString() });
  viewer.addHandler('zoom', onZoom);
  moveableUpdateEvents.forEach((event) => {
    viewer.addHandler(event, updateMoveableTarget);
  });
};

const removeEventListeners = (viewer: OpenSeadragon.Viewer) => {
  viewer.removeHandler('add-overlay', onOverlayAdded);
  viewer.removeHandler('update-overlay', onLocationChange);
  // viewer.removeHandler('viewport-change', setViewportChangingValue);
  // viewer.removeHandler('viewport-change', onViewChangingEnd);
  viewer.removeHandler('viewport-change', () => { lastViewportChangeDate.value = (new Date()).toString() });
  viewer.removeHandler('zoom', onZoom);
  moveableUpdateEvents.forEach((event) => {
    viewer.removeHandler(event, updateMoveableTarget);
  });
};

onMounted(() => {
  if (injected) {
    injected.isReady()
      .then((res) => {
        const viewer = res.getViewer();
        addEventListeners(viewer);
        if (el.value) addOverlay(viewer, el.value, props.options);
      });
  }
});

onBeforeUnmount(async () => {
  const viewer = getViewer();
  if (viewer && el.value) {
    removeEventListeners(viewer);
    if (el.value) removeOverlay(viewer, el.value);
    if (mouseTracker.value) mouseTracker.value.destroy();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _mergeProps({
      ref_key: "el",
      ref: el,
      class: ["relative", props.active ? 'z-30' : 'z-0']
    }, _ctx.$attrs), [
      _renderSlot(_ctx.$slots, "default")
    ], 16),
    (!isViewportChanging.value)
      ? (_openBlock(), _createBlock(_unref(Moveable), {
          key: 0,
          ref_key: "moveable",
          ref: moveable,
          "data-highlight": props.highlight,
          "data-service": _ctx.$store.getters['service/service'],
          resizable: __props.resizable,
          target: el.value,
          onResize: updateMoveablePosition,
          onResizeEnd: updateOverlayPosition
        }, null, 8, ["data-highlight", "data-service", "resizable", "target"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})